// Libraries
import { loadInstagramPosts } from "@customer-app/state/actionCreators/integrations";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";
import { Box, Container, Link, Stack } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { SECTION } from "@smartrr/shared/entities/CustomerPortalTheme";
import { noawait } from "@smartrr/shared/utils/noawait";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo } from "react";

import { AquariusBanner } from "./AquariusBanner";
import { FailedPaymentBanner } from "./components/FailedPaymentBanner";
import { LoyaltyHandler } from "./components/Loyalty/components/LoyaltyHandler";
import { ManageOutOfStockAndUnavailable } from "./components/ManageOutOfStockAndUnavailable";
import { OrderHistory } from "./components/OrderHistory";
import { QuickbuyHandler } from "./components/Quickbuy";
import { Settings } from "./components/Settings";
import { Instagram } from "./components/SocialEmbeds/Instagram";
import { Subscriptions } from "./components/Subscription";
import { NoSubscriptions } from "./components/Subscription/NoSubscriptions";
import { Trending } from "./components/Trending";
import { InnerWrapper } from "./constants";
import { useUnavailableVariantsAndDeliveries } from "./hooks/useUnavailableVariantsAndDeliveries";
import { useBillStore } from "./store/BillsStore";
import { useSubscriptionStoreInit } from "./store/SubscriptionActionStore";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { GlobalModal, IModal, ModalComponent } from "../GlobalModal";

import { useSmartrrTheme } from "../hooks/useSmartrrTheme";
import { useOrganization } from "../utils";
import { usePurchaseStates } from "./hooks/usePurchaseStates";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";
import { getActiveAndPausedCustomerPurchaseStates } from "@smartrr/shared/utils/getActiveAndPausedCustomerPurchaseStates";
import { SellingPlanGroupStoreAccess } from "@customer-app/state/zustand/sellingPlanGroup";

export type ToggleModalFn = <M extends ModalComponent>(modalType: M, props: IModal[M], close?: boolean) => void;

export const useIsLoading = () => {
  const isThemeLoading = useSmartrrCustomerSelector(state => state.theme.isLoading);
  const isPurchaseStateLoading = useSmartrrCustomerSelector(state => state.purchaseStates.isLoading);
  const isCustomerLoading = useSmartrrCustomerSelector(state => state.auth.isLoading);
  const isPurchasablesLoading = useSmartrrCustomerSelector(state => state.purchasables.isLoading);
  return isThemeLoading || isPurchaseStateLoading || isPurchasablesLoading || isCustomerLoading;
};

export const HandleInstagram = () => {
  const instagramPosts = useSmartrrCustomerSelector(state => state.instagramPosts);
  const organization = useOrganization();
  const theme = useSmartrrTheme();
  const dispatch = useSmartrrCustomerDispatch();

  if (!isEmpty(organization.instagramUserData) && !instagramPosts.firstLoad) {
    dispatch(loadInstagramPosts());
  }

  const showInstagram =
    !isEmpty(instagramPosts) &&
    organization &&
    theme.modernThemeSettings?.sections.instagram.display &&
    organization?.instagramUserData?.username !== undefined;

  return (
    <React.Fragment>
      {showInstagram ? (
        <Instagram postData={instagramPosts!} userData={organization?.instagramUserData!} theme={theme} />
      ) : null}
    </React.Fragment>
  );
};

export function AquariusInner(): JSX.Element {
  const isLoading = useIsLoading();
  const theme = useSmartrrTheme();
  useSubscriptionStoreInit();

  const { actions: billingActions } = useBillStore();

  const sectionOrder = theme.modernThemeSettings?.sectionsOrder;

  const purchaseStates = usePurchaseStates();
  const deliveryActions = DeliveryAccess.useActions();
  const org = useOrganization();
  const upcomingDelivery = DeliveryAccess.useUpcomingDelivery();
  const hasActiveDeliveries = DeliveryAccess.useHasActiveDeliveries();
  const hasPausedDeliveries = DeliveryAccess.useHasPausedDeliveries();
  const actions = SellingPlanGroupStoreAccess.useActions();

  const relevantPurchaseStates = useMemo(
    () => getActiveAndPausedCustomerPurchaseStates(purchaseStates),
    [purchaseStates]
  );

  useEffect(() => {
    deliveryActions.update(purchaseStates, org);
  }, [purchaseStates]);

  const { shouldBannersBeShown: showManageOutOfStockBanner } = useUnavailableVariantsAndDeliveries();

  const subscriptionIndex = useMemo(
    () => (sectionOrder ? sectionOrder.indexOf(SECTION.SUBSCRIPTION) : 0),
    [sectionOrder]
  );
  const orderHistoryIndex = useMemo(
    () => (sectionOrder ? sectionOrder.indexOf(SECTION.ORDERHISTORY) : 1),
    [sectionOrder]
  );

  const settingsIndex = useMemo(
    () => (sectionOrder ? sectionOrder.indexOf(SECTION.SETTINGS) : 2),
    [sectionOrder]
  );

  const loyaltyIndex = useMemo(() => sectionOrder?.indexOf(SECTION.LOYALTY), [sectionOrder]);

  const instagramIndex = useMemo(() => sectionOrder?.indexOf(SECTION.INSTAGRAM), [sectionOrder]);

  const trendingIndex = useMemo(() => sectionOrder?.indexOf(SECTION.TRENDING), [sectionOrder]);

  useEffect(() => {
    noawait(async () => {
      if (upcomingDelivery) {
        await billingActions.loadFailedBills(upcomingDelivery.purchaseState);
      }
    });
  }, [upcomingDelivery]);

  useEffect(() => {
    noawait(async () => {
      await actions.fetchSellingPlans();
    });
  }, []);

  if (isLoading) {
    return <React.Fragment />;
  }
  return (
    <InnerWrapper id="smartrr_inner">
      <Container>
        {showManageOutOfStockBanner ? <ManageOutOfStockAndUnavailable /> : null}
        {upcomingDelivery?.purchaseState ? (
          <FailedPaymentBanner purchaseState={upcomingDelivery.purchaseState} />
        ) : null}
        {theme.modernThemeSettings?.sections.banner.display && theme.modernThemeSettings?.sections.banner.text ? (
          <AquariusBanner className={prefixClassName("announcement-banner")}>
            <span dangerouslySetInnerHTML={{ __html: theme.modernThemeSettings?.sections.banner.text }} />
          </AquariusBanner>
        ) : null}

        <Stack spacing="40px">
          {[
            <React.Fragment key={subscriptionIndex}>
              {!hasActiveDeliveries && !hasPausedDeliveries && !relevantPurchaseStates.length ? (
                <React.Fragment>
                  <ErrorBoundary>
                    <NoSubscriptions />
                    {theme.modernThemeSettings?.sections.subscriptions.addOns ? <QuickbuyHandler /> : null}
                  </ErrorBoundary>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <ErrorBoundary>
                    {theme.modernThemeSettings?.sections.subscriptions.display ? (
                      <Subscriptions upcomingDelivery={upcomingDelivery!} />
                    ) : (
                      <React.Fragment />
                    )}
                  </ErrorBoundary>
                </React.Fragment>
              )}
            </React.Fragment>,
            <React.Fragment key={orderHistoryIndex}>
              <ErrorBoundary>
                {theme.modernThemeSettings?.sections.orderHistory.display ? <OrderHistory /> : null}
              </ErrorBoundary>
            </React.Fragment>,
            <React.Fragment key={settingsIndex}>
              <ErrorBoundary>
                {theme.modernThemeSettings?.sections.settings.display ? <Settings /> : null}
              </ErrorBoundary>
            </React.Fragment>,
            ...(sectionOrder?.includes(SECTION.LOYALTY)
              ? [
                  <React.Fragment key={loyaltyIndex}>
                    <ErrorBoundary>
                      <LoyaltyHandler />
                    </ErrorBoundary>
                  </React.Fragment>,
                ]
              : []),
            ...(sectionOrder?.includes(SECTION.INSTAGRAM)
              ? [
                  <React.Fragment key={instagramIndex}>
                    <ErrorBoundary>
                      <HandleInstagram />
                    </ErrorBoundary>
                  </React.Fragment>,
                ]
              : []),
            ...(sectionOrder?.includes(SECTION.TRENDING)
              ? [
                  <React.Fragment key={trendingIndex}>
                    <ErrorBoundary>
                      {upcomingDelivery && theme.modernThemeSettings?.sections.trending.display ? (
                        <Trending delivery={upcomingDelivery!} />
                      ) : null}
                    </ErrorBoundary>
                  </React.Fragment>,
                ]
              : []),
          ].sort((a, b) => Number(a?.key) - (b! && Number(b.key)))}

          <Stack
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            sx={{
              "& hr": {
                borderColor: theme.modernLineDividers,
                borderWidth: "1px",
                margin: "0px",
                marginTop: "20px",
                width: "100%",
              },
            }}
          >
            <hr style={{ marginBottom: "40px" }} />
            <Box
              sx={{
                paddingTop: "20px",
                marginBottom: "20px",
              }}
            >
              <Link
                href="/account/logout"
                variant="caption1"
                underline="hover"
                sx={{
                  color: theme.modernCaptionFontColor,
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              >
                {localize("header.tabs.logOut")}
              </Link>
            </Box>
          </Stack>
        </Stack>
        <GlobalModal />
      </Container>
    </InnerWrapper>
  );
}
